import api from "./axiosInstance";

/**
 * Report Service
 * Handles report generation API calls for Admin/Head/Manager users
 * 
 * NOTE: This feature is NOT available to Site Engineers or Site Supervisors
 */

/** Matches backend `report.validation` frequency filter (optional; omit or `all` = no filter). */
export type PMReportFrequencyFilter =
    | 'all'
    | 'Daily'
    | 'Weekly'
    | 'Fortnightly'
    | 'Monthly'
    | 'Quarterly'
    | 'Half-Yearly'
    | 'Annual'
    | 'Yearly';

export const PM_REPORT_FREQUENCY_OPTIONS: { value: PMReportFrequencyFilter; label: string }[] = [
    { value: 'all', label: 'All frequencies' },
    { value: 'Daily', label: 'Daily' },
    { value: 'Weekly', label: 'Weekly' },
    { value: 'Fortnightly', label: 'Fortnightly' },
    { value: 'Monthly', label: 'Monthly' },
    { value: 'Quarterly', label: 'Quarterly' },
    { value: 'Half-Yearly', label: 'Half-yearly' },
    { value: 'Annual', label: 'Annual' },
    { value: 'Yearly', label: 'Yearly' },
];

/**
 * Task statuses that can be excluded from the PM report via the
 * advanced filter. Matches backend `task.status` enum, plus the
 * synthetic `pending` group (unassigned / due / overdue).
 */
export type PMReportExcludableStatus =
    | 'pass'
    | 'fail'
    | 'skip'
    | 'pending'
    | 'inprogress'
    | 'hold'
    | 'inactive';

export const PM_REPORT_STATUS_OPTIONS: { value: PMReportExcludableStatus; label: string; description: string }[] = [
    { value: 'pass', label: 'Pass', description: 'Completed successfully' },
    { value: 'fail', label: 'Fail', description: 'Completed with failure' },
    { value: 'pending', label: 'Pending', description: 'Unassigned / due / overdue' },
    { value: 'skip', label: 'Skip', description: 'Skipped / superseded' },
    { value: 'inprogress', label: 'In progress', description: 'Currently being worked on' },
    { value: 'hold', label: 'On hold', description: 'Paused tasks' },
    { value: 'inactive', label: 'Inactive', description: 'Deactivated / deleted' },
];

export interface IReportRequest {
    projectId: string;
    startDate: string; // ISO date string
    endDate: string;   // ISO date string
    /** Restrict report rows to tasks with this maintenance frequency */
    frequency?: PMReportFrequencyFilter;
    /** Exclude tasks whose status matches any of these values */
    excludeStatuses?: PMReportExcludableStatus[];
}

export interface IReportError {
    message: string;
    code?: number;
}

/**
 * Result of preventive maintenance report generation (blob + optional record count from header).
 */
export interface IGeneratePreventiveMaintenanceResult {
    blob: Blob;
    recordCount?: number;
}

/**
 * Generate preventive maintenance report
 * Downloads Excel file with task history for selected project and date range
 *
 * @param request - Report parameters (projectId, startDate, endDate)
 * @returns Blob and optional record count (from X-Record-Count header)
 */
/** Build the POST body, stripping empty/`all` filters so the backend sees only meaningful overrides. */
const buildReportRequestBody = (request: IReportRequest): Record<string, unknown> => {
    const body: Record<string, unknown> = {
        projectId: request.projectId,
        startDate: request.startDate,
        endDate: request.endDate,
    };
    if (request.frequency && request.frequency !== 'all') {
        body['frequency'] = request.frequency;
    }
    if (request.excludeStatuses && request.excludeStatuses.length > 0) {
        body['excludeStatuses'] = request.excludeStatuses;
    }
    return body;
};

export const generatePreventiveMaintenanceReport = async (
    request: IReportRequest
): Promise<IGeneratePreventiveMaintenanceResult> => {
    const body = buildReportRequestBody(request);
    const response = await api.post(
        '/reports/preventive-maintenance',
        body,
        {
            responseType: 'blob',
            timeout: 120000 // 2 minutes timeout for large reports
        }
    );
    const blob = response.data as Blob;
    const recordCountHeader = response.headers?.['x-record-count'];
    const parsed =
        recordCountHeader != null && recordCountHeader !== ''
            ? parseInt(String(recordCountHeader), 10)
            : NaN;
    const recordCount = Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
    return recordCount !== undefined ? { blob, recordCount } : { blob };
};

/**
 * Generate preventive maintenance report as PDF (same filters and data as Excel).
 */
export const generatePreventiveMaintenanceReportPdf = async (
    request: IReportRequest
): Promise<IGeneratePreventiveMaintenanceResult> => {
    const body = buildReportRequestBody(request);
    const response = await api.post(
        '/reports/preventive-maintenance/pdf',
        body,
        {
            responseType: 'blob',
            timeout: 120000
        }
    );
    const blob = response.data as Blob;
    const recordCountHeader = response.headers?.['x-record-count'];
    const parsed =
        recordCountHeader != null && recordCountHeader !== ''
            ? parseInt(String(recordCountHeader), 10)
            : NaN;
    const recordCount = Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
    return recordCount !== undefined ? { blob, recordCount } : { blob };
};

/**
 * Trigger download of PDF blob as file
 */
export const downloadPdfFile = (
    blob: Blob,
    projectName: string,
    startDate: string,
    endDate: string
): void => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const sanitizedProjectName = projectName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
    const formattedStart = startDate.split('T')[0].replace(/-/g, '');
    const formattedEnd = endDate.split('T')[0].replace(/-/g, '');
    link.download = `PM_Report_${sanitizedProjectName}_${formattedStart}_${formattedEnd}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

/**
 * Preview data interfaces
 */
export interface IReportTaskPreview {
    equipmentName: string;
    checkpointName: string;
    specification: string;
    frequency: string;
    plannedDate: string;
    assignedTo: string;
    startTime: string;
    endTime: string;
    actualDate: string;
    delay: number;
    beforePictureCount: number;
    afterPictureCount: number;
    beforeImages: string[];
    afterImages: string[];
    outcome: string;
    remarks: string;
    issueDescription: string;
    issuePriority: string;
    actionTaken: string;
    issuePlannedDate: string;
    resolutionDate: string;
    issueDelay: number;
    issueBeforeImages: string[];
    issueAfterImages: string[];
    status: string;
}

export interface IReportProjectPreview {
    projectName: string;
    projectCode: string;
    location: string;
    dcCapacity: string;
    acCapacity: string;
    maintenanceIncharge: string;
    siteIncharge: string;
}

export interface IReportSummary {
    totalTasks: number;
    completedTasks: number;
    pendingTasks: number;
    passCount: number;
    failCount: number;
    totalDelays: number;
    issuesRaised: number;
    overdueTasks: number;
}

export interface IReportPreviewResponse {
    project: IReportProjectPreview;
    tasks: IReportTaskPreview[];
    summary: IReportSummary;
}

/**
 * Get preview data for preventive maintenance report
 * Returns formatted data to display in the UI before generating Excel
 * 
 * @param request - Report parameters (projectId, startDate, endDate)
 * @returns Preview data with project info, tasks, and summary
 */
export const getPreventiveMaintenancePreview = async (
    request: IReportRequest
): Promise<IReportPreviewResponse> => {
    const body = buildReportRequestBody(request);
    const response = await api.post(
        '/reports/preventive-maintenance/preview',
        body,
        {
            timeout: 60000 // 1 minute timeout
        }
    );
    return response.data.data;
};

/**
 * Trigger download of Excel blob as file
 * 
 * @param blob - Excel file blob
 * @param projectName - Project name for filename
 * @param startDate - Start date for filename
 * @param endDate - End date for filename
 */
export const downloadExcelFile = (
    blob: Blob,
    projectName: string,
    startDate: string,
    endDate: string
): void => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    // Format filename: PM_Report_ProjectName_StartDate_EndDate.xlsx
    const sanitizedProjectName = projectName.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30);
    const formattedStart = startDate.split('T')[0].replace(/-/g, '');
    const formattedEnd = endDate.split('T')[0].replace(/-/g, '');
    const filename = `PM_Report_${sanitizedProjectName}_${formattedStart}_${formattedEnd}.xlsx`;

    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

/**
 * Get date range for quick select options
 */
export const getDateRangeForPeriod = (period: 'today' | 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth'): { startDate: Date; endDate: Date } => {
    const today = new Date();
    let startDate: Date;
    let endDate: Date;

    switch (period) {
        case 'today': {
            startDate = new Date(today);
            endDate = new Date(today);
            break;
        }
        case 'thisWeek': {
            const dayOfWeek = today.getDay();
            startDate = new Date(today);
            startDate.setDate(today.getDate() - dayOfWeek); // Sunday
            endDate = new Date(today);
            break;
        }
        case 'lastWeek': {
            const dayOfWeek = today.getDay();
            startDate = new Date(today);
            startDate.setDate(today.getDate() - dayOfWeek - 7);
            endDate = new Date(today);
            endDate.setDate(today.getDate() - dayOfWeek - 1);
            break;
        }
        case 'thisMonth': {
            startDate = new Date(today.getFullYear(), today.getMonth(), 1);
            endDate = new Date(today);
            break;
        }
        case 'lastMonth': {
            startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            endDate = new Date(today.getFullYear(), today.getMonth(), 0);
            break;
        }
    }

    return { startDate, endDate };
};

/**
 * Report generation log interface
 */
export interface IReportLog {
    id: string;
    projectId: string;
    projectName: string;
    projectCode: string;
    location: string;
    generatedBy: string;
    generatedByEmail: string;
    generatedAt: string;
    dateRange: {
        startDate: string;
        endDate: string;
    };
    fileName: string;
    fileSize: number;
}

export interface IPaginationMeta {
    total: number;
    page: number;
    limit: number;
    pages: number;
}

export interface IReportLogsResponse {
    success: boolean;
    data: IReportLog[];
    meta: IPaginationMeta;
    total?: number; // fallback/legacy
}

/**
 * Get report generation logs with pagination
 * 
 * @param params - Filter and pagination parameters
 * @returns List of report generation logs and metadata
 */
export const getReportLogs = async (
    params?: { projectIds?: string[]; page?: number; limit?: number }
): Promise<{ data: IReportLog[], meta: IPaginationMeta }> => {
    const queryParams: any = {};
    if (params?.projectIds && params.projectIds.length > 0) {
        queryParams.projectIds = params.projectIds.join(',');
    }
    if (params?.page) {
        queryParams.page = params.page;
    }
    if (params?.limit) {
        queryParams.limit = params.limit;
    }

    const response = await api.get('/reports/logs', { params: queryParams });

    // Handle both old and new response structures gracefully
    const data = response.data.data || [];
    const meta = response.data.meta || {
        total: response.data.total || data.length,
        page: params?.page || 1,
        limit: params?.limit || 50,
        pages: Math.ceil((response.data.total || data.length) / (params?.limit || 50))
    };

    return { data, meta };
};

/**
 * Download a previously generated report from logs
 * 
 * @param logId - Report log ID
 * @returns Blob containing Excel file
 */
export const downloadReportFromLog = async (logId: string): Promise<Blob> => {
    const response = await api.get(`/reports/logs/${logId}/download`, {
        responseType: 'blob',
        timeout: 60000
    });
    return response.data;
};

export interface IProjectResourcePerformanceResult {
    blob: Blob;
    /** Total resource rows across all projects (from `X-Record-Count`). */
    recordCount?: number;
}

/**
 * Download the manager snapshot PDF: every active project's resources with
 * Completed / Pending / Overdue counts as of right now. Returned as a Blob
 * so the caller can save it locally and share via WhatsApp.
 */
export const downloadProjectResourcePerformancePdf = async (): Promise<IProjectResourcePerformanceResult> => {
    const response = await api.get('/reports/project-resource-performance/pdf', {
        responseType: 'blob',
        timeout: 120000
    });
    const blob = response.data as Blob;
    const recordCountHeader = response.headers?.['x-record-count'];
    const parsed =
        recordCountHeader != null && recordCountHeader !== ''
            ? parseInt(String(recordCountHeader), 10)
            : NaN;
    const recordCount = Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
    return recordCount !== undefined ? { blob, recordCount } : { blob };
};

/**
 * Download the resource-wise snapshot PDF: each resource grouped with their
 * projects and Completed / Pending / Overdue counts as of right now.
 */
export const downloadResourceWisePerformancePdf = async (): Promise<IProjectResourcePerformanceResult> => {
    const response = await api.get('/reports/resource-wise-performance/pdf', {
        responseType: 'blob',
        timeout: 120000
    });
    const blob = response.data as Blob;
    const recordCountHeader = response.headers?.['x-record-count'];
    const parsed =
        recordCountHeader != null && recordCountHeader !== ''
            ? parseInt(String(recordCountHeader), 10)
            : NaN;
    const recordCount = Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
    return recordCount !== undefined ? { blob, recordCount } : { blob };
};

/**
 * Download the dense one-page Resource Overview PDF: a single row per
 * resource with top projects, progress bar, done/pending/overdue counts and
 * a verdict pill. Idle / critical resources are highlighted at the top so
 * managers can spot non-performers immediately.
 */
export const downloadResourceOverviewPdf = async (): Promise<IProjectResourcePerformanceResult> => {
    const response = await api.get('/reports/resource-overview/pdf', {
        responseType: 'blob',
        timeout: 120000
    });
    const blob = response.data as Blob;
    const recordCountHeader = response.headers?.['x-record-count'];
    const parsed =
        recordCountHeader != null && recordCountHeader !== ''
            ? parseInt(String(recordCountHeader), 10)
            : NaN;
    const recordCount = Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
    return recordCount !== undefined ? { blob, recordCount } : { blob };
};

/**
 * Download the Sorigin-branded Daily Snapshot PDF: tier-grouped resource
 * list (Good / Mid / Not Doing / Not Opened App Yet) with per-row project
 * list, completion progress bar, and last-login pill.
 */
export const downloadDailySnapshotPdf = async (): Promise<IProjectResourcePerformanceResult> => {
    const response = await api.get('/reports/daily-snapshot/pdf', {
        responseType: 'blob',
        timeout: 120000
    });
    const blob = response.data as Blob;
    const recordCountHeader = response.headers?.['x-record-count'];
    const parsed =
        recordCountHeader != null && recordCountHeader !== ''
            ? parseInt(String(recordCountHeader), 10)
            : NaN;
    const recordCount = Number.isInteger(parsed) && parsed >= 0 ? parsed : undefined;
    return recordCount !== undefined ? { blob, recordCount } : { blob };
};

/**
 * Trigger browser download for a generic PDF blob with a caller-supplied
 * filename. Used by the manager dashboard snapshot button (and any other
 * one-off PDF download that doesn't fit the PM report shape).
 */
export const downloadBlobAsFile = (blob: Blob, fileName: string): void => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

// ---------------------------------------------------------------------------
// Daily Site Engineer Report (Manager dashboard)
// ---------------------------------------------------------------------------

export type DailyReportFormat = 'pdf' | 'xlsx';

/**
 * Downloads the manager-scoped Daily Site Engineer Report for one date
 * (YYYY-MM-DD, an IST calendar day) in the requested format and saves it
 * via the shared blob-download helper.
 */
export const downloadDailySiteEngineerReport = async (
    date: string,
    format: DailyReportFormat
): Promise<void> => {
    const response = await api.get(`/reports/daily-site-engineer/${format}`, {
        params: { date },
        responseType: 'blob',
        timeout: 120000,
    });
    downloadBlobAsFile(response.data as Blob, `daily-site-engineer-report-${date}.${format}`);
};

export const reportService = {
    generatePreventiveMaintenanceReport,
    generatePreventiveMaintenanceReportPdf,
    getPreventiveMaintenancePreview,
    downloadExcelFile,
    downloadPdfFile,
    getDateRangeForPeriod,
    getReportLogs,
    downloadReportFromLog,
    downloadProjectResourcePerformancePdf,
    downloadResourceWisePerformancePdf,
    downloadResourceOverviewPdf,
    downloadDailySnapshotPdf,
    downloadBlobAsFile,
    downloadDailySiteEngineerReport,
};

